<template>
  <v-autocomplete
    v-model="internalValue"
    :items="filteredIcons"
    item-title="name"
    item-value="icon"
    label="Select an Icon"
    clearable
    dense
    outlined>
    <template v-slot:chip="{ props, item }">
      <v-icon class="mr-3">{{ item.raw.icon }}</v-icon>
      {{ item.raw.name }}
    </template>
    <template v-slot:item="{ props, item }">
      <v-list-item
        v-bind="props"
        :prepend-icon="item.raw.icon"
        :title="item.raw.name"></v-list-item>
    </template>
  </v-autocomplete>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
// Props and Emits
const props = defineProps({
  modelValue: Object, // Expecting an icon object with 'name' and 'icon' properties
});
const emit = defineEmits(['update:modelValue']);
// Internal value to handle v-model binding
const internalValue = ref(props.modelValue || null);

// Watch for changes in internalValue and emit 'update:modelValue' for v-model support
watch(internalValue, (newValue) => {
  emit('update:modelValue', newValue);
});
const searchQuery = ref('');
// Computed property to filter icons based on the search query
const filteredIcons = computed(() => {
  if (!searchQuery.value) return mdiIcons;
  return mdiIcons.filter((icon) =>
    icon.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});
const mdiIcons = [
  { name: 'Numeric 1 Box', icon: 'mdi-numeric-1-box' },
  { name: 'Numeric 2 Box', icon: 'mdi-numeric-2-box' },
  { name: 'Numeric 3 Box', icon: 'mdi-numeric-3-box' },
  { name: 'Numeric 4 Box', icon: 'mdi-numeric-4-box' },
  { name: 'Numeric 5 Box', icon: 'mdi-numeric-5-box' },
  { name: 'Numeric 6 Box', icon: 'mdi-numeric-6-box' },
  { name: 'Numeric 7 Box', icon: 'mdi-numeric-7-box' },
  { name: 'Numeric 8 Box', icon: 'mdi-numeric-8-box' },
  { name: 'Numeric 9 Box', icon: 'mdi-numeric-9-box' },
  { name: 'Numeric 0 Box', icon: 'mdi-numeric-0-box' },
  { name: 'Numeric 0 Circle', icon: 'mdi-numeric-0-circle' },
  { name: 'Numeric 1 Circle', icon: 'mdi-numeric-1-circle' },
  { name: 'Numeric 2 Circle', icon: 'mdi-numeric-2-circle' },
  { name: 'Numeric 3 Circle', icon: 'mdi-numeric-3-circle' },
  { name: 'Numeric 4 Circle', icon: 'mdi-numeric-4-circle' },
  { name: 'Numeric 5 Circle', icon: 'mdi-numeric-5-circle' },
  { name: 'Numeric 6 Circle', icon: 'mdi-numeric-6-circle' },
  { name: 'Numeric 7 Circle', icon: 'mdi-numeric-7-circle' },
  { name: 'Numeric 8 Circle', icon: 'mdi-numeric-8-circle' },
  { name: 'Numeric 9 Circle', icon: 'mdi-numeric-9-circle' },
  { name: 'Numeric 0', icon: 'mdi-numeric-0' },
  { name: 'Numeric 1', icon: 'mdi-numeric-1' },
  { name: 'Numeric 2', icon: 'mdi-numeric-2' },
  { name: 'Numeric 3', icon: 'mdi-numeric-3' },
  { name: 'Numeric 4', icon: 'mdi-numeric-4' },
  { name: 'Numeric 5', icon: 'mdi-numeric-5' },
  { name: 'Numeric 6', icon: 'mdi-numeric-6' },
  { name: 'Numeric 7', icon: 'mdi-numeric-7' },
  { name: 'Numeric 8', icon: 'mdi-numeric-8' },
  { name: 'Numeric 9', icon: 'mdi-numeric-9' },
  { name: 'Numeric 0 Box Multiple', icon: 'mdi-numeric-0-box-multiple' },
  { name: 'Numeric 1 Box Multiple', icon: 'mdi-numeric-1-box-multiple' },
  { name: 'Numeric 2 Box Multiple', icon: 'mdi-numeric-2-box-multiple' },
  { name: 'Numeric 3 Box Multiple', icon: 'mdi-numeric-3-box-multiple' },
  { name: 'Account', icon: 'mdi-account' },
  { name: 'Account Circle', icon: 'mdi-account-circle' },
  { name: 'Account Group', icon: 'mdi-account-group' },
  { name: 'Alert', icon: 'mdi-alert' },
  { name: 'Alert Circle', icon: 'mdi-alert-circle' },
  { name: 'Alert Octagon', icon: 'mdi-alert-octagon' },
  { name: 'Alert Outline', icon: 'mdi-alert-outline' },
  { name: 'Alarm', icon: 'mdi-alarm' },
  { name: 'Alarm Check', icon: 'mdi-alarm-check' },
  { name: 'Alarm Multiple', icon: 'mdi-alarm-multiple' },
  { name: 'Alarm Off', icon: 'mdi-alarm-off' },
  { name: 'Alphabetical', icon: 'mdi-alphabetical' },
  { name: 'Airplane', icon: 'mdi-airplane' },
  { name: 'Airplane Landing', icon: 'mdi-airplane-landing' },
  { name: 'Airplane Takeoff', icon: 'mdi-airplane-takeoff' },
  { name: 'Android', icon: 'mdi-android' },
  { name: 'Apple', icon: 'mdi-apple' },
  { name: 'Application', icon: 'mdi-application' },
  { name: 'Archive', icon: 'mdi-archive' },
  { name: 'Arrow Down', icon: 'mdi-arrow-down' },
  { name: 'Arrow Left', icon: 'mdi-arrow-left' },
  { name: 'Arrow Right', icon: 'mdi-arrow-right' },
  { name: 'Arrow Up', icon: 'mdi-arrow-up' },
  { name: 'Auto Fix', icon: 'mdi-auto-fix' },
  { name: 'Battery', icon: 'mdi-battery' },
  { name: 'Battery Charging', icon: 'mdi-battery-charging' },
  { name: 'Battery Minus', icon: 'mdi-battery-minus' },
  { name: 'Battery Plus', icon: 'mdi-battery-plus' },
  { name: 'Bell', icon: 'mdi-bell' },
  { name: 'Bell Off', icon: 'mdi-bell-off' },
  { name: 'Bluetooth', icon: 'mdi-bluetooth' },
  { name: 'Book', icon: 'mdi-book' },
  { name: 'Book Open', icon: 'mdi-book-open' },
  { name: 'Bookmark', icon: 'mdi-bookmark' },
  { name: 'Bookmark Outline', icon: 'mdi-bookmark-outline' },
  { name: 'Briefcase', icon: 'mdi-briefcase' },
  { name: 'Brightness', icon: 'mdi-brightness-6' },
  { name: 'Calendar', icon: 'mdi-calendar' },
  { name: 'Calendar Check', icon: 'mdi-calendar-check' },
  { name: 'Calendar Remove', icon: 'mdi-calendar-remove' },
  { name: 'Camera', icon: 'mdi-camera' },
  { name: 'Camera Front', icon: 'mdi-camera-front' },
  { name: 'Camera Rear', icon: 'mdi-camera-rear' },
  { name: 'Cancel', icon: 'mdi-cancel' },
  { name: 'Car', icon: 'mdi-car' },
  { name: 'Car Battery', icon: 'mdi-car-battery' },
  { name: 'Card Account Details', icon: 'mdi-card-account-details' },
  { name: 'Cart', icon: 'mdi-cart' },
  { name: 'Cart Off', icon: 'mdi-cart-off' },
  { name: 'Cash', icon: 'mdi-cash' },
  { name: 'Cellphone', icon: 'mdi-cellphone' },
  { name: 'Cellphone Off', icon: 'mdi-cellphone-off' },
  { name: 'Check', icon: 'mdi-check' },
  { name: 'Check Circle', icon: 'mdi-check-circle' },
  { name: 'Checkbox Blank', icon: 'mdi-checkbox-blank' },
  { name: 'Checkbox Marked', icon: 'mdi-checkbox-marked' },
  { name: 'Chevron Down', icon: 'mdi-chevron-down' },
  { name: 'Chevron Left', icon: 'mdi-chevron-left' },
  { name: 'Chevron Right', icon: 'mdi-chevron-right' },
  { name: 'Chevron Up', icon: 'mdi-chevron-up' },
  { name: 'Circle', icon: 'mdi-circle' },
  { name: 'City', icon: 'mdi-city' },
  { name: 'Clipboard', icon: 'mdi-clipboard' },
  { name: 'Clock', icon: 'mdi-clock' },
  { name: 'Close', icon: 'mdi-close' },
  { name: 'Cloud', icon: 'mdi-cloud' },
  { name: 'Cloud Download', icon: 'mdi-cloud-download' },
  { name: 'Cloud Upload', icon: 'mdi-cloud-upload' },
  { name: 'Code Tags', icon: 'mdi-code-tags' },
  { name: 'Cog', icon: 'mdi-cog' },
  { name: 'Comment', icon: 'mdi-comment' },
  { name: 'Comment Check', icon: 'mdi-comment-check' },
  { name: 'Comment Outline', icon: 'mdi-comment-outline' },
  { name: 'Compass', icon: 'mdi-compass' },
  { name: 'Content Copy', icon: 'mdi-content-copy' },
  { name: 'Credit Card', icon: 'mdi-credit-card' },
  { name: 'Crosshairs', icon: 'mdi-crosshairs' },
  { name: 'Delete', icon: 'mdi-delete' },
  { name: 'Delete Forever', icon: 'mdi-delete-forever' },
  { name: 'Domain', icon: 'mdi-domain' },
  { name: 'Dots Horizontal', icon: 'mdi-dots-horizontal' },
  { name: 'Dots Vertical', icon: 'mdi-dots-vertical' },
  { name: 'Download', icon: 'mdi-download' },
  { name: 'Earth', icon: 'mdi-earth' },
  { name: 'Email', icon: 'mdi-email' },
  { name: 'Email Outline', icon: 'mdi-email-outline' },
  { name: 'Exit To App', icon: 'mdi-exit-to-app' },
  { name: 'Eye', icon: 'mdi-eye' },
  { name: 'Eye Off', icon: 'mdi-eye-off' },
  { name: 'Factory', icon: 'mdi-factory' },
  { name: 'File', icon: 'mdi-file' },
  { name: 'File Document', icon: 'mdi-file-document' },
  { name: 'File Image', icon: 'mdi-file-image' },
  { name: 'Filter', icon: 'mdi-filter' },
  { name: 'Flag', icon: 'mdi-flag' },
  { name: 'Folder', icon: 'mdi-folder' },
  { name: 'Folder Open', icon: 'mdi-folder-open' },
  { name: 'Format Bold', icon: 'mdi-format-bold' },
  { name: 'Format Italic', icon: 'mdi-format-italic' },
  { name: 'Format Underline', icon: 'mdi-format-underline' },
  { name: 'Fullscreen', icon: 'mdi-fullscreen' },
  { name: 'Gift', icon: 'mdi-gift' },
  { name: 'Google', icon: 'mdi-google' },
  { name: 'Heart', icon: 'mdi-heart' },
  { name: 'Help Circle', icon: 'mdi-help-circle' },
  { name: 'Home', icon: 'mdi-home' },
  { name: 'Image', icon: 'mdi-image' },
  { name: 'Inbox', icon: 'mdi-inbox' },
  { name: 'Information', icon: 'mdi-information' },
  { name: 'Key', icon: 'mdi-key' },
  { name: 'Label', icon: 'mdi-label' },
  { name: 'Laptop', icon: 'mdi-laptop' },
  { name: 'Lightbulb', icon: 'mdi-lightbulb' },
  { name: 'Link', icon: 'mdi-link' },
  { name: 'Lock', icon: 'mdi-lock' },
  { name: 'Map', icon: 'mdi-map' },
  { name: 'Menu', icon: 'mdi-menu' },
  { name: 'Message', icon: 'mdi-message' },
  { name: 'Microphone', icon: 'mdi-microphone' },
  { name: 'Minus', icon: 'mdi-minus' },
  { name: 'Monitor', icon: 'mdi-monitor' },
  { name: 'Moon', icon: 'mdi-moon' },
  { name: 'Mouse', icon: 'mdi-mouse' },
  { name: 'Music', icon: 'mdi-music' },
  { name: 'Notebook', icon: 'mdi-notebook' },
  { name: 'Open In New', icon: 'mdi-open-in-new' },
  { name: 'Pause', icon: 'mdi-pause' },
  { name: 'Pencil', icon: 'mdi-pencil' },
  { name: 'Phone', icon: 'mdi-phone' },
  { name: 'Play', icon: 'mdi-play' },
  { name: 'Plus', icon: 'mdi-plus' },
  { name: 'Power', icon: 'mdi-power' },
  { name: 'Prin ter', icon: 'mdi-printer' },
  { name: 'Puzzle', icon: 'mdi-puzzle' },
  { name: 'Refresh', icon: 'mdi-refresh' },
  { name: 'Rocket', icon: 'mdi-rocket' },
  { name: 'School', icon: 'mdi-school' },
  { name: 'Search', icon: 'mdi-magnify' },
  { name: 'Settings', icon: 'mdi-cog' },
  { name: 'Share', icon: 'mdi-share' },
  { name: 'Shield', icon: 'mdi-shield' },
  { name: 'Shopping', icon: 'mdi-shopping' },
  { name: 'Sleep', icon: 'mdi-sleep' },
  { name: 'Star', icon: 'mdi-star' },
  { name: 'Stop', icon: 'mdi-stop' },
  { name: 'Store', icon: 'mdi-store' },
  { name: 'Tablet', icon: 'mdi-tablet' },
  { name: 'Tag', icon: 'mdi-tag' },
  { name: 'Traffic Light', icon: 'mdi-traffic-light' },
  { name: 'Trash', icon: 'mdi-trash-can' },
  { name: 'Truck', icon: 'mdi-truck' },
  { name: 'Umbrella', icon: 'mdi-umbrella' },
  { name: 'Upload', icon: 'mdi-upload' },
  { name: 'User', icon: 'mdi-account' },
  { name: 'Video', icon: 'mdi-video' },
  { name: 'View Dashboard', icon: 'mdi-view-dashboard' },
  { name: 'Wallet', icon: 'mdi-wallet' },
  { name: 'Watch', icon: 'mdi-watch' },
  { name: 'Wifi', icon: 'mdi-wifi' },
  { name: 'Window Maximize', icon: 'mdi-window-maximize' },
  { name: 'Zoom In', icon: 'mdi-magnify-plus' },
  { name: 'Zoom Out', icon: 'mdi-magnify-minus' },
  { name: 'Light Switch Off', icon: 'mdi-light-switch-off' },
  { name: 'Light Switch On', icon: 'mdi-light-switch' },
];
</script>
<style scoped>
.v-list-item-avatar {
  min-width: 40px;
}
</style>
